import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Sparkles } from 'lucide-react';
import type { ProfileResponse } from '@masha/shared';
import {
  api,
  ApiError,
  type CarouselStyleDto,
  type SessionGenerateResponse,
  type SessionGetResponse,
} from '../../lib/api';
import { haptic } from '../../lib/tma';
import { notifyTurboCap } from '../../lib/turboNotice';
import { Button, Spinner, Toast } from '../ui';
import { StyleGrid } from './StyleGrid';
import { StylePreviewModal } from './StylePreviewModal';

const NICK_RE = /^[A-Za-z0-9._]{1,30}$/;

export interface StyleStepProps {
  sessionId: number;
  initialStyle: string | null;
  initialNick: string | null;
  /** Text of the back link; own-text sessions go back to the text, not the answers. */
  backLabel?: string;
  onBack: () => void;
  onGenerated: (resp: SessionGenerateResponse) => void;
}

export function StyleStep({
  sessionId,
  initialStyle,
  initialNick,
  backLabel = 'К ответам',
  onBack,
  onGenerated,
}: StyleStepProps) {
  const qc = useQueryClient();
  const profile = qc.getQueryData<ProfileResponse>(['profile']);
  const [style, setStyle] = useState<string | null>(initialStyle);
  const [nick, setNick] = useState<string>(
    (initialNick ?? profile?.user.username ?? '').replace(/^@+/, ''),
  );
  const [preview, setPreview] = useState<CarouselStyleDto | null>(null);

  const stylesQuery = useQuery({
    queryKey: ['carousel-styles'],
    queryFn: () => api.listCarouselStyles(),
    staleTime: 5 * 60_000,
    // Right after a deploy the API is still rendering previews: poll until every style has its deck.
    refetchInterval: (query) =>
      query.state.data?.styles.some((s) => s.preview_urls.length === 0) ? 10_000 : false,
  });

  const nickValid = NICK_RE.test(nick);

  const generate = useMutation({
    mutationFn: async () => {
      if (!style) throw new Error('Выберите стиль');
      await api.setSessionStyle(sessionId, { style_code: style, nick });
      qc.setQueryData<SessionGetResponse>(['session', sessionId], (old) =>
        old ? { ...old, style_code: style, nick } : old,
      );
      return api.generateSession(sessionId);
    },
    onSuccess: (data) => {
      haptic('success');
      notifyTurboCap(qc, data.charged_via);
      qc.invalidateQueries({ queryKey: ['profile'] });
      qc.setQueryData<SessionGetResponse>(['session', sessionId], (old) =>
        old
          ? {
              ...old,
              session: { ...old.session, status: 'completed', free_rewrites_used: 0 },
              last_generation: data.generation,
              rewrite_cost: 'free' as const,
              render: data.render,
              style_code: data.render?.style_code ?? old.style_code,
            }
          : old,
      );
      onGenerated(data);
    },
    onError: () => {
      haptic('error');
      // The server-side style PATCH may have gone through even though generate
      // failed afterwards (e.g. session flipped to `failed`). Re-fetch so the
      // page reflects the real session state instead of re-issuing PATCH /style
      // against a session that has moved on.
      qc.invalidateQueries({ queryKey: ['session', sessionId] });
    },
  });

  if (generate.isPending) {
    return (
      <div className="min-h-screen p-6 max-w-lg mx-auto flex flex-col items-center justify-center text-center safe-top safe-bottom">
        <Spinner size="lg" className="mb-4" />
        <h1 className="text-lg font-semibold mb-1">Собираем карусель</h1>
        <p className="text-sm text-tg-hint">
          Обычно занимает 30-60 секунд. Слайды придут и в Telegram файлами.
        </p>
      </div>
    );
  }

  const error = generate.error;
  const isPaywall = error instanceof ApiError && error.status === 402;
  const errorMessage = error
    ? isPaywall
      ? 'Не хватает кредитов. Пополните баланс, чтобы продолжить.'
      : `Не удалось собрать карусель: ${error.message}`
    : null;

  return (
    <div className="min-h-screen p-4 max-w-lg mx-auto pb-8 safe-top safe-bottom">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1 text-sm text-tg-hint hover:text-tg-text transition-colors mb-3"
      >
        <ArrowLeft size={16} />
        {backLabel}
      </button>

      <header className="mb-4">
        <h1 className="text-xl font-bold mb-1">Выберите стиль</h1>
        <p className="text-sm text-tg-hint leading-snug">
          Нажмите на стиль, чтобы посмотреть все слайды. Стиль можно поменять после генерации
          бесплатно.
        </p>
      </header>

      {stylesQuery.isLoading && (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      )}
      {stylesQuery.data && (
        <StyleGrid
          styles={stylesQuery.data.styles}
          value={style}
          onChange={setStyle}
          onPreview={setPreview}
        />
      )}

      <label className="block mt-5 mb-1 text-xs font-semibold uppercase tracking-wider text-tg-hint">
        Ник в Instagram
      </label>
      <div className="flex items-center rounded-xl bg-tg-section-bg border border-tg-hint/30 focus-within:border-brand focus-within:ring-2 focus-within:ring-brand/40 transition-colors">
        <span className="pl-3 text-tg-hint">@</span>
        <input
          type="text"
          value={nick}
          onChange={(e) => setNick(e.target.value.replace(/^@+/, ''))}
          placeholder="ваш_ник"
          autoCapitalize="none"
          autoCorrect="off"
          className="w-full bg-transparent px-2 py-3 focus:outline-none"
        />
      </div>
      <p className="text-xs text-tg-hint mt-1 mb-5">
        Печатается на каждом слайде. Латиница, цифры, точка и подчёркивание.
      </p>

      {errorMessage && (
        <Toast tone={isPaywall ? 'warning' : 'destructive'} className="mb-3">
          {errorMessage}
        </Toast>
      )}

      <Button
        onClick={() => generate.mutate()}
        disabled={!style || !nickValid}
        variant="primary"
        size="lg"
        fullWidth
        leftIcon={<Sparkles size={18} />}
      >
        {error ? 'Повторить' : 'Сгенерировать карусель'}
      </Button>

      <StylePreviewModal
        style={preview}
        selected={preview !== null && preview.code === style}
        onSelect={setStyle}
        onClose={() => setPreview(null)}
      />
    </div>
  );
}
